import CtaArrow from "@/components/svg/CtaArrow";
import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { PrismicNextLink } from "@prismicio/next";

/**
 * @param {{ ctas: import("@prismicio/client").Repeatable<import("@prismicio/client").LinkField> }} props
 */
const ContactCtas = ({ ctas }) => {
  const filledCtas = Array.isArray(ctas)
    ? ctas.filter((cta) => isFilled.link(cta))
    : [];

  if (filledCtas.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col items-center gap-5 md:w-auto md:flex-row md:justify-center">
      {filledCtas.map((cta, index) =>
        cta.variant === "Primary" ? (
          <PrismicNextLink
            prefetch={false}
            key={cta.key ?? index}
            field={cta}
            className="cta cta-text w-full bg-vert-50 text-blanc md:w-auto"
          >
            {cta.text}
            <CtaArrow className="text-blanc" />
          </PrismicNextLink>
        ) : (
          <PrismicNextLink
            prefetch={false}
            key={cta.key ?? index}
            field={cta}
            className="cta cta-text w-full text-blanc underline underline-offset-2 md:w-auto"
          >
            {cta.text}
          </PrismicNextLink>
        ),
      )}
    </div>
  );
};

const titleComponents = {
  heading2: ({ children }) => (
    <h2 className="h2-n lg:dk text-center text-beige-30">{children}</h2>
  ),
  heading3: ({ children }) => (
    <h3 className="h2-n lg:dk text-center text-beige-30">{children}</h3>
  ),
};

const textComponents = {
  paragraph: ({ children }) => (
    <p className="p-xl-n lt text-center text-beige-30">{children}</p>
  ),
  strong: ({ children }) => <strong className="md">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hyperlink: ({ children, node }) => (
    <a href={node.data.url} className="underline underline-offset-2">
      {children}
    </a>
  ),
};

/**
 * @typedef {import("@prismicio/client").Content.ContactSlice} ContactSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ContactSlice>} ContactProps
 * @type {import("react").FC<ContactProps>}
 */
const Contact = ({ slice }) => {
  const { title, text, ctas } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno relative overflow-hidden bg-bleu-50 padding-x py-15 lg:py-[74px]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 left-0 top-0 z-0 h-full opacity-10"
        style={{
          backgroundImage: "url('/icons/aztk-tilable-logo.svg')",
          backgroundRepeat: "repeat",
          backgroundSize: "50px",
        }}
      />

      <div className="framing-lg relative flex flex-col items-center gap-[60px]">
        {(isFilled.richText(title) || isFilled.richText(text)) && (
          <div className="flex w-full max-w-[652px] flex-col items-center gap-[11px]">
            {isFilled.richText(title) && (
              <PrismicRichText field={title} components={titleComponents} />
            )}
            {isFilled.richText(text) && (
              <PrismicRichText field={text} components={textComponents} />
            )}
          </div>
        )}
        <ContactCtas ctas={ctas} />
      </div>
    </section>
  );
};

export default Contact;
